import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { toast } from 'sonner';
import axios from 'axios';
import { MessageCircle, Loader2, Car, User, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => { fetchConversations(); }, []);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/marketplace/messages`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setConversations(res.data.conversations || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide flex items-center gap-3 mb-8">
          <MessageCircle className="text-primary" /> Meine Nachrichten
        </h1>

        {conversations.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border">
            <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Noch keine Nachrichten.</p>
            <Link to="/marketplace" className="text-primary text-sm hover:underline mt-2 inline-block">Zum Marktplatz</Link>
          </div>
        ) : (
          <div className="space-y-2">
            {conversations.map(conv => (
              <Link
                key={conv.listing_id}
                to={`/marketplace/${conv.listing_id}`}
                className="flex items-center gap-4 p-4 bg-card border border-border hover:border-primary/50 transition-colors"
                data-testid={`conversation-${conv.listing_id}`}
                onClick={() => {
                  // Will open messages panel on the listing detail page
                  sessionStorage.setItem('open_messages', conv.listing_id);
                }}
              >
                {/* Listing thumbnail */}
                <div className="w-14 h-14 flex-shrink-0 overflow-hidden border border-border">
                  {conv.listing?.images?.length > 0 ? (
                    <img src={conv.listing.images[0].url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted/30 flex items-center justify-center"><Car size={20} className="text-muted-foreground/30" /></div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{conv.listing?.title || 'Inserat'}</span>
                    {conv.unread_count > 0 && (
                      <span className="bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">{conv.unread_count}</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                    <User size={12} /> {conv.other_member_name}
                  </p>
                  <p className="text-sm text-muted-foreground truncate mt-0.5">{conv.last_message?.text}</p>
                </div>

                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-muted-foreground">{new Date(conv.last_message?.created_at).toLocaleDateString('de-CH')}</p>
                  <ChevronRight size={16} className="text-muted-foreground mt-1 ml-auto" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
